import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { MessageSquareWarning, Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['general', 'salary', 'workplace', 'colleague', 'management', 'facility', 'other'];

export default function ComplaintsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [responseDialog, setResponseDialog] = useState({ open: false, id: null, status: 'resolved' });
  const [responseText, setResponseText] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'general', description: '', is_anonymous: false });

  const isManager = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await api.get('/complaints', { params });
      setItems(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error('Subject and description are required'); return;
    }
    try {
      await api.post('/complaints', form);
      toast.success('Complaint submitted. HR has been notified.');
      setDialogOpen(false);
      setForm({ subject: '', category: 'general', description: '', is_anonymous: false });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleResolve = async () => {
    try {
      await api.put(`/complaints/${responseDialog.id}`, { status: responseDialog.status, admin_response: responseText });
      toast.success(`Complaint ${responseDialog.status}`);
      setResponseDialog({ open: false, id: null, status: 'resolved' });
      setResponseText('');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const statusColor = (s) => {
    if (s === 'resolved') return 'bg-emerald-100 text-emerald-700';
    if (s === 'closed') return 'bg-neutral-200 text-neutral-700';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="complaints-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Complaints & Grievances</h1>
          <p className="text-neutral-500 text-sm mt-1">{isManager ? 'Review and resolve employee complaints' : 'Raise a complaint — HR will respond privately'}</p>
        </div>
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="complaint-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" onClick={() => setDialogOpen(true)} data-testid="raise-complaint-button">
            <Plus className="h-4 w-4 mr-2" /> Raise Complaint
          </Button>
        </div>
      </div>

      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-16 bg-neutral-100 rounded animate-pulse" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-neutral-500">
              <MessageSquareWarning className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
              <p className="text-sm">No complaints here.</p>
              {!isManager && <p className="text-xs mt-1">Something troubling you? Click "Raise Complaint" above.</p>}
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {items.map(c => (
                <div key={c.id} className="p-5" data-testid={`complaint-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-heading font-bold text-neutral-900 text-base">{c.subject}</h3>
                        <Badge className={`text-xs capitalize ${statusColor(c.status)}`}>{c.status.replace('_', ' ')}</Badge>
                        <Badge variant="outline" className="text-[10px] capitalize border-neutral-200">{c.category}</Badge>
                      </div>
                      <p className="text-sm text-neutral-700 whitespace-pre-wrap">{c.description}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500 mt-2">
                        <span>By <strong>{c.employee_name}</strong></span>
                        <span>·</span>
                        <span>{new Date(c.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      {c.admin_response && (
                        <div className="mt-3 pl-3 border-l-2 border-emerald-400 bg-emerald-50/40 p-3 rounded-sm">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">Admin Response</p>
                          <p className="text-sm text-neutral-700 whitespace-pre-wrap">{c.admin_response}</p>
                        </div>
                      )}
                    </div>
                    {isManager && c.status === 'open' && (
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" className="text-xs text-blue-600 h-8"
                          onClick={() => { setResponseDialog({ open: true, id: c.id, status: 'in_progress' }); setResponseText(c.admin_response || ''); }}>
                          In Progress
                        </Button>
                        <Button size="sm" variant="ghost" className="text-xs text-emerald-600 h-8"
                          onClick={() => { setResponseDialog({ open: true, id: c.id, status: 'resolved' }); setResponseText(c.admin_response || ''); }} data-testid={`resolve-complaint-${c.id}`}>
                          <Check className="h-3 w-3 mr-1" /> Resolve
                        </Button>
                      </div>
                    )}
                    {isManager && c.status === 'in_progress' && (
                      <Button size="sm" variant="ghost" className="text-xs text-emerald-600 h-8"
                        onClick={() => { setResponseDialog({ open: true, id: c.id, status: 'resolved' }); setResponseText(c.admin_response || ''); }} data-testid={`resolve-complaint-${c.id}`}>
                        <Check className="h-3 w-3 mr-1" /> Resolve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raise Complaint Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="raise-complaint-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">Raise a Complaint</DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">Your complaint is visible only to HR / management.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Subject *</Label>
              <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                placeholder="Briefly describe the issue" className="rounded-sm" data-testid="complaint-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="complaint-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Description *</Label>
              <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={4} placeholder="Share the full details..." className="rounded-sm" data-testid="complaint-description-input" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_anonymous}
                onChange={e => setForm(p => ({ ...p, is_anonymous: e.target.checked }))}
                className="rounded" data-testid="complaint-anonymous-checkbox" />
              <span className="text-sm text-neutral-700">Submit anonymously</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSubmit} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="complaint-submit-button">
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Response Dialog */}
      <Dialog open={responseDialog.open} onOpenChange={(o) => setResponseDialog(p => ({ ...p, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{responseDialog.status === 'resolved' ? 'Resolve Complaint' : 'Mark In Progress'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm font-medium">Response / Notes</Label>
            <Textarea value={responseText} onChange={e => setResponseText(e.target.value)} rows={4} className="rounded-sm"
              placeholder="Your response to the employee..." data-testid="complaint-response-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResponseDialog({ open: false, id: null, status: 'resolved' })} className="rounded-sm">Cancel</Button>
            <Button onClick={handleResolve} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="complaint-response-submit">
              {responseDialog.status === 'resolved' ? 'Resolve' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
